
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight } from "lucide-react";
import PropertyAllRisksForm from "@/components/quote-forms/PropertyAllRisksForm";
import ThirdPartyLiabilityForm from "@/components/quote-forms/ThirdPartyLiabilityForm";
import WorkersCompensationForm from "@/components/quote-forms/WorkersCompensationForm";
import EmployeeMedicalForm from "@/components/quote-forms/EmployeeMedicalForm";
import TenantFooter from "@/components/layout/TenantFooter";
import CyberInsuranceForm from "@/components/quote-forms/CyberInsuranceForm";
import FleetInsuranceForm from "@/components/quote-forms/FleetInsuranceForm";
import ProfessionalIndemnityForm from "@/components/quote-forms/ProfessionalIndemnityForm";
import BusinessInterruptionForm from "@/components/quote-forms/BusinessInterruptionForm";
import ProductLiabilityForm from "@/components/quote-forms/ProductLiabilityForm";
import SMEPackageForm from "@/components/quote-forms/SMEPackageForm";
import ContactDetailsForm from "@/components/quote-forms/ContactDetailsForm";
import { FormData, ContactDetailsFormData } from "@/types/forms";
import { SubmissionService } from "@/services/submissionService";
import { useToast } from "@/hooks/use-toast";
import StickyNavigation from "@/components/layout/StickyNavigation";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { getPageTitle } from "@/utils/PageUtils";

const productTitles = {
  "property-all-risks": "Property All Risks Insurance",
  "third-party-liability": "Third Party Liability Insurance",
  "workers-compensation": "Workers Compensation Insurance",
  "employee-medical": "Employee Medical Insurance",
  "cyber-insurance": "Cyber Insurance",
  "fleet-insurance": "Fleet Insurance",
  "professional-indemnity": "Professional Indemnity Insurance",
  "business-interruption": "Business Interruption Insurance",
  "product-liability": "Product Liability Insurance",
  "sme-package": "SME Package (Office) Insurance"
};

const QuoteForm = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;
  const { tenant } = useTenant();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pageTitle = "Get Your Quote"
  useEffect(() => {
    document.title = getPageTitle(pageTitle);
  }, []); 

  if (!productId || !productTitles[productId as keyof typeof productTitles]) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <StickyNavigation />
        <div className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <Button onClick={() => navigate("/")} className="mt-4">
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const productTitle = productTitles[productId as keyof typeof productTitles];
  const totalSteps = 2; // Product form + Contact details

  const handleFormSubmit = async (data: FormData) => {
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final submission - save to database
      setIsSubmitting(true);

      try {
        const contactDetails = data as ContactDetailsFormData;

        // Resolve tenant id: prefer context, but replace dev placeholders with real UUID by subdomain
        let tenantId: string | undefined = tenant?.id;
        if (!tenantId || tenantId.startsWith('dev-')) {
          const host = window.location.hostname;
          const subFromHost = host.endsWith('.localhost') ? host.split('.')[0] : undefined;
          const subFromPath = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/)?.[1];
          const sub = subFromHost || subFromPath;
          if (sub && sub !== 'www') {
            const { data: tdata, error: terr } = await supabase
              .from('tenants')
              .select('id')
              .eq('subdomain', sub)
              .maybeSingle();
            if (!terr && tdata?.id) {
              tenantId = tdata.id;
            }
          }
        }
        // Final fallback: in local development allow default tenant; in production require a configured tenant
        if (!tenantId) {
          const hostname = window.location.hostname;
          const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
          if (isLocal) {
            tenantId = '00000000-0000-0000-0000-000000000001';
          } else {
            // Root domain (e.g. yallacompare.com) — use default tenant so
            // quote submissions still work on the main site
            tenantId = '00000000-0000-0000-0000-000000000001';
          }
        }

        if (!tenantId) throw new Error('Tenant information not available');

        // Try to save submission, but don't fail the whole flow if product doesn't exist
        try {
          await SubmissionService.createSubmission({
            productId: productId!,
            productSlug: productId!,
            formData: updatedFormData,
            contactDetails,
            tenantId
          });
        } catch (submissionError) {
          console.warn('⚠️ Could not save submission to database (product may not exist):', submissionError);
          // Continue anyway - we'll still get the AIG quote
        }

        let aigQuoteState: unknown = null;

        if (productId === 'sme-package') {
          try {
            const smeData = updatedFormData as any;

            const productBySegment: Record<string, string> = {
              office: 'aig-bop-sme-yallacompare-bop-office',
              retail: 'aig-bop-sme-yallacompare-bop-retail',
              fnb: 'aig-bop-sme-yallacompare-bop-fnb',
            };

            const segmentKey = (smeData.segment as 'office' | 'retail' | 'fnb') || 'office';
            const productCode = productBySegment[segmentKey] || productBySegment.office;

            const fullName = (contactDetails.name || '').trim();
            const [firstName, ...restName] = fullName.split(' ');
            const lastName = restName.join(' ') || firstName || '';

            const covers: Record<string, unknown> = { form_name: 'Covers' };

            if (smeData.annualWages) {
              covers['workm-annual-wages-of-employees'] = String(smeData.annualWages);
            }

            if (smeData.publicLiabilityLimit) {
              covers['public-liability'] = smeData.publicLiabilityLimit;
            }

            if (segmentKey === 'office') {
              if (smeData.buildingSumInsured) covers['prope-building-sum-insured'] = String(smeData.buildingSumInsured);
              if (smeData.contentsSumInsured) covers['prope-contents'] = String(smeData.contentsSumInsured);
              if (smeData.equipmentSumInsured) covers['prope-equipment'] = String(smeData.equipmentSumInsured);
              if (smeData.portableEquipmentSumInsured) covers['prope-portable-equipment'] = String(smeData.portableEquipmentSumInsured);
              if (smeData.biGrossProfit) covers['busin-business-interruption-gross-profit-value'] = String(smeData.biGrossProfit);
              if (smeData.lossOfRent) covers['busin-loss-of-rent-annual-rent'] = String(smeData.lossOfRent);
              if (smeData.increasedCostOfWorking) covers['busin-increased-cost-of-working'] = String(smeData.increasedCostOfWorking);
            }

            const aigPayload = {
              form_name: 'SME',
              product: productCode,
              nature_of_business: smeData.natureOfBusiness,
              start_date: smeData.startDate,
              company_name: smeData.companyName,
              commercial_registry_no: smeData.commercialRegistryNo,
              first_name: firstName,
              last_name: lastName,
              address: smeData.address,
              po_box: smeData.poBox,
              area: smeData.area,
              emirate: smeData.emirate,
              country: smeData.country || 'United Arab Emirates',
              mobile: contactDetails.phone,
              telephone: contactDetails.phone,
              email: contactDetails.email,
              tax_registration_number: smeData.taxRegistrationNumber,
              vat_legal_name: smeData.vatLegalName,
              vat_address: smeData.vatAddress,
              Covers: covers,
              risk_assessment: smeData.riskAssessment === 'yes' ? 'true' : 'false',
              existing_insurance: smeData.existingInsurance === 'yes' ? 'true' : 'false',
              representations: !!smeData.representationsAccepted,
              quote: true,
              policy_number: null,
            };

            const { data: aigData, error: aigError } = await supabase.functions.invoke('aig-quote', {
              body: aigPayload,
            });

            if (aigError) {
              console.error('Error calling AIG quote function:', aigError);
            } else {
              aigQuoteState = aigData;
            }
          } catch (aigError) {
            console.error('Unexpected error while calling AIG quote function:', aigError);
          }
        }

        // For SME, always go to the price page (it will show price if available or a friendly message if not)
        if (productId === 'sme-package') {
          navigate(tLink('/quote-price'), {
            state: { aigQuote: aigQuoteState, formData: updatedFormData },
          });
        } else {
          toast({
            title: "Quote Submitted Successfully!",
            description: "We've received your details and will share your quote shortly.",
          });
          navigate(tLink('/quote-submitted'), {
            state: { aigQuote: aigQuoteState },
          });
        }
      } catch (error) {
        console.error('Error submitting quote:', error);
        toast({
          title: "Submission Failed",
          description: "There was an error submitting your quote. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const renderProductForm = () => {
    const commonProps = {
      onSubmit: handleFormSubmit,
      initialData: formData
    };

    switch (productId) {
      case "property-all-risks":
        return <PropertyAllRisksForm {...commonProps} />;
      case "third-party-liability":
        return <ThirdPartyLiabilityForm {...commonProps} />;
      case "workers-compensation":
        return <WorkersCompensationForm {...commonProps} />;
      case "employee-medical":
        return <EmployeeMedicalForm {...commonProps} />;
      case "cyber-insurance":
        return <CyberInsuranceForm {...commonProps} />;
      case "fleet-insurance":
        return <FleetInsuranceForm {...commonProps} />;
      case "professional-indemnity":
        return <ProfessionalIndemnityForm {...commonProps} />;
      case "business-interruption":
        return <BusinessInterruptionForm {...commonProps} />;
      case "product-liability":
        return <ProductLiabilityForm {...commonProps} />;
      case "sme-package":
        return <SMEPackageForm {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <StickyNavigation />
      <div className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Homepage
            </Button>

            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{pageTitle}</h1>
              <p className="text-xl text-gray-600 mb-4">{productTitle}</p>
              <div className="text-sm text-gray-500">
                Step {currentStep} of {totalSteps}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Form Content */}
          <Card>
            <CardHeader>
              <CardTitle>
                {currentStep === 1 ? "Product Information" : "Contact Details"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentStep === 1 ? (
                renderProductForm()
              ) : (
                <ContactDetailsForm
                  onSubmit={handleFormSubmit}
                  initialData={formData}
                  onBack={() => setCurrentStep(1)}
                  isSubmitting={isSubmitting}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Tenant-aware footer to match homepage */}
      <TenantFooter />
    </div>
  );
};

export default QuoteForm;
